import { Box, Inline, Pressable, Text, xcss } from '@atlaskit/primitives';
import React, { type ReactNode } from 'react';

export interface CompactRadioPickerOption<Value extends string> {
  icon?: ReactNode;
  label: string;
  value: Value;
}

export type CompactRadioPickerProps<Value extends string> = {
  ariaLabel: string;
  onChange: (value: Value) => void;
  options: CompactRadioPickerOption<Value>[];
  value: Value;
};

const pickerStyles = xcss({
  alignItems: 'center',
  backgroundColor: 'color.background.input',
  borderColor: 'color.border',
  borderRadius: 'radius.large',
  borderStyle: 'solid',
  borderWidth: 'border.width',
  display: 'inline-flex',
  padding: 'space.050',
  width: '100%'
});

const optionStyles = xcss({
  alignItems: 'center',
  backgroundColor: 'color.background.neutral.subtle',
  borderRadius: 'radius.medium',
  color: 'color.text.subtle',
  display: 'inline-flex',
  flexBasis: 0,
  flexGrow: 1,
  justifyContent: 'center',
  minHeight: '32px',
  position: 'relative',
  ':hover': {
    backgroundColor: 'color.background.neutral.subtle.hovered'
  },
  ':active': {
    backgroundColor: 'color.background.neutral.subtle.pressed'
  }
});

const selectedOptionStyles = xcss({
  backgroundColor: 'color.background.selected',
  color: 'color.text.selected',
  ':hover': {
    backgroundColor: 'color.background.selected.hovered'
  },
  ':active': {
    backgroundColor: 'color.background.selected.pressed'
  }
});

const selectedOptionBorderStyles = xcss({
  borderColor: 'color.border.selected',
  borderRadius: 'radius.medium',
  borderStyle: 'solid',
  borderWidth: 'border.width',
  inset: 'space.0',
  pointerEvents: 'none',
  position: 'absolute'
});

const CompactRadioPicker = <Value extends string>({
  ariaLabel,
  onChange,
  options,
  value
}: CompactRadioPickerProps<Value>) => {
  return (
    <Box aria-label={ariaLabel} role="radiogroup" xcss={pickerStyles}>
      <Inline alignBlock="center" grow="fill" space="space.050" shouldWrap={false}>
        {options.map((option) => {
          const isSelected = option.value === value;

          return (
            <Pressable
              key={option.value}
              aria-checked={isSelected}
              onClick={() => onChange(option.value)}
              role="radio"
              xcss={[optionStyles, isSelected && selectedOptionStyles]}
            >
              {isSelected && <Box xcss={selectedOptionBorderStyles} />}
              <Inline alignBlock="center" space="space.075" shouldWrap={false}>
                {option.icon}
                <Text color="inherit" maxLines={1}>
                  {option.label}
                </Text>
              </Inline>
            </Pressable>
          );
        })}
      </Inline>
    </Box>
  );
};

export default CompactRadioPicker;
